import React, { useMemo, useState } from "react";
import moment from "moment-timezone";
import { useLogs } from "../../logs/hooks/useLogs";
import { useBillingPlans } from "../../billing/hooks/useBillingPlans";
import StatusBadge from "../../../shared/components/common/StatusBadge";
import Table from "../../../shared/components/common/Table";
import PageHeader from "../../../shared/components/common/PageHeader";
import Tooltip from "../../../shared/components/common/Tooltip";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import SpeedIcon from "@mui/icons-material/Speed";
import TimerIcon from "@mui/icons-material/Timer";
import LogsToolbar from "../../logs/components/LogsToolbar";
import FilterModal from "../../logs/components/FilterModal";
import "../../logs/Logs.css";

import MetricCard from "./MetricCard";
import VoicemailVolumeChart from "./VoicemailVolumeChart";
import TranscriptionStats from "./TranscriptionStats";
import TopUsers from "./TopUsers";

const formatNumber = (value) => Number(value || 0).toLocaleString();
const DASHBOARD_TIMEZONE = "America/Los_Angeles";

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const calculateAverage = (total, count) => {
  if (!count) return 0;
  return total / count;
};

const readNumericField = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const getDurationSecondsFromLog = (log) => {
  const source = log?._original || log;

  const durationMs = readNumericField(source, ["duration_ms", "durationMs"]);
  if (durationMs !== null) {
    return Math.max(0, durationMs) / 1000;
  }

  return readNumericField(source, [
    "message_duration_seconds",
    "message_duration",
    "duration_seconds",
    "duration",
  ]);
};

const parseTimestampMs = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const parseLogDate = (log) => {
  const source = log?._original || log;
  const value = source?.created_at || source?.createdAt || log?.created_time;
  if (!value) return null;

  const parsed = moment.tz(value, DASHBOARD_TIMEZONE);
  return parsed.isValid() ? parsed : null;
};

const getProcessingSecondsFromLog = (log) => {
  const source = log?._original || log;

  const createdAtMs = parseTimestampMs(
    source?.created_at || source?.createdAt || log?.created_time,
  );
  const deliveredAtMs = parseTimestampMs(
    source?.delivery_timestamp || source?.deliveryTimestamp,
  );

  if (createdAtMs !== null && deliveredAtMs !== null) {
    const processingSeconds = (deliveredAtMs - createdAtMs) / 1000;
    if (Number.isFinite(processingSeconds) && processingSeconds >= 0) {
      return processingSeconds;
    }
  }

  return readNumericField(source, [
    "processing_time_seconds",
    "processing_seconds",
    "processing_time",
  ]);
};

const hasSmsTranscription = (log) => {
  const source = log?._original || log;
  const smsDeliveryStatus = String(source?.sms_delivery_status || "").trim();
  const smsDeliveryTimestamp = String(source?.sms_delivery_timestamp || "").trim();

  return Boolean(smsDeliveryStatus || smsDeliveryTimestamp);
};

const normalizeStatusToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getNormalizedStatus = (log) => {
  const jobStatus = normalizeStatusToken(log?.job_status);
  const deliveryStatus = normalizeStatusToken(log?.delivery_status);
  const deliveryStatusMap = {
    DELIVERED: "delivered",
    FAILED: "failed",
    PROCESSING: "processing",
  };

  if (deliveryStatusMap[deliveryStatus]) {
    return deliveryStatusMap[deliveryStatus];
  }

  if (
    ["PROCESS_COMPLETED", "DELIVERED", "SUCCESS"].includes(jobStatus) ||
    ["DELIVERED", "SUCCESS"].includes(deliveryStatus)
  ) {
    return "delivered";
  }

  if (
    jobStatus.includes("FAIL") ||
    jobStatus.includes("ERROR") ||
    jobStatus.includes("INVALID") ||
    jobStatus.includes("NO_COMPANY") ||
    jobStatus.includes("NOT_DELIVERED") ||
    deliveryStatus.includes("FAIL") ||
    deliveryStatus.includes("ERROR") ||
    deliveryStatus.includes("INVALID") ||
    deliveryStatus.includes("NO_COMPANY") ||
    deliveryStatus.includes("NOT_DELIVERED")
  ) {
    return "failed";
  }

  if (
    [
      "UPLOADED",
      "PROCESSING",
      "IN_PROGRESS",
      "PENDING",
      "QUEUED",
      "STARTED",
    ].includes(jobStatus) ||
    ["PROCESSING", "IN_PROGRESS", "PENDING", "QUEUED", "STARTED"].includes(
      deliveryStatus,
    )
  ) {
    return "processing";
  }

  return "";
};

const toStatusLabel = (statusValue) => {
  if (statusValue === "delivered") return "Delivered";
  if (statusValue === "failed") return "Failed";
  if (statusValue === "processing") return "Processing";
  return "Not Delivered";
};

const Dashboard = () => {
  const {
    filteredLogs,
    isLoading,
    currentPage,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    deliveryStatusFilter,
    setDeliveryStatusFilter,
  } = useLogs();
  const { currentPlan } = useBillingPlans();
  const [spreadTable, setSpreadTable] = useState(false);
  const [dateRange, setDateRange] = useState("all");
  const [showFilterModal, setShowFilterModal] = useState(false);

  const dateRangeOptions = [
    { name: "All Time", value: "all" },
    { name: "Today", value: "today" },
    { name: "This Week", value: "thisweek" },
    { name: "Last Week", value: "lastweek" },
  ];

  const statusOptions = [
    { name: "All Status", value: "" },
    { name: "Failed", value: "failed" },
    { name: "Processing", value: "processing" },
    { name: "Delivered", value: "delivered" },
  ];

  const extractCallerId = (subject) => {
    const match = subject?.match(/from (.+)/i);
    return match ? match[1] : "-";
  };

  const displayLogs = useMemo(() => {
    let filtered = filteredLogs;

    if (dateRange !== "all") {
      const now = moment();
      filtered = filtered.filter((log) => {
        const logDate = moment(log.created_time);
        switch (dateRange) {
          case "today":
            return logDate.isSame(now, "day");
          case "thisweek":
            return logDate.isSame(now, "week");
          case "lastweek":
            return logDate.isSame(now.clone().subtract(1, "week"), "week");
          default:
            return true;
        }
      });
    }

    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((log) => {
        const userName = (log.email_from_name || "").toLowerCase();
        const callerId = (log.email_from_address || "").toLowerCase();
        const deliverTo = (log.to_email_addresses || "").toLowerCase();
        return (
          userName.includes(query) ||
          callerId.includes(query) ||
          deliverTo.includes(query)
        );
      });
    }

    if (deliveryStatusFilter) {
      filtered = filtered.filter((log) => {
        return (
          getNormalizedStatus(log) === String(deliveryStatusFilter).toLowerCase()
        );
      });
    }

    return filtered.map((log) => ({
      id: log.id || log.job_name,
      createdDateTime: moment
        .tz(new Date(log.created_time), "America/Los_Angeles")
        .format("YYYY-MM-DD HH:mm:ss"),
      callerId: extractCallerId(log.email_subject || ""),
      userName: log.email_from_name || "User",
      deliverTo: log.to_email_addresses || "john.miller@companymail.com",
      status: toStatusLabel(getNormalizedStatus(log)),
      originalLog: log,
    }));
  }, [filteredLogs, dateRange, searchQuery, deliveryStatusFilter]);

  const hasAnyLogs = (filteredLogs || []).length > 0;

  const dashboardData = useMemo(() => {
    const totalLogs = filteredLogs.length;
    const deliveredCount = filteredLogs.filter(
      (log) => getNormalizedStatus(log) === "delivered",
    ).length;
    const failedCount = filteredLogs.filter(
      (log) => getNormalizedStatus(log) === "failed",
    ).length;

    const smsTranscriptions = filteredLogs.filter(hasSmsTranscription).length;

    const successRate = totalLogs > 0 ? (deliveredCount / totalLogs) * 100 : 0;
    const failureRate = totalLogs > 0 ? (failedCount / totalLogs) * 100 : 0;

    const durationSummary = filteredLogs.reduce(
      (acc, log) => {
        const durationSeconds = getDurationSecondsFromLog(log);
        if (durationSeconds === null) {
          return acc;
        }

        return {
          totalSeconds: acc.totalSeconds + durationSeconds,
          recordsWithDuration: acc.recordsWithDuration + 1,
        };
      },
      { totalSeconds: 0, recordsWithDuration: 0 },
    );

    const durationSecondsTotal = durationSummary.totalSeconds;

    const processingSummary = filteredLogs.reduce(
      (acc, log) => {
        const processingSeconds = getProcessingSecondsFromLog(log);
        if (processingSeconds === null) {
          return acc;
        }

        return {
          totalSeconds: acc.totalSeconds + processingSeconds,
          recordsWithProcessing: acc.recordsWithProcessing + 1,
        };
      },
      { totalSeconds: 0, recordsWithProcessing: 0 },
    );

    const processingSecondsTotal = processingSummary.totalSeconds;

    const avgProcessingSeconds = calculateAverage(
      processingSecondsTotal,
      processingSummary.recordsWithProcessing,
    );
    const avgMessageDuration = calculateAverage(
      durationSecondsTotal,
      durationSummary.recordsWithDuration,
    );

    const formatMinutesToReadable = (minutes) => {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      if (hours > 0) {
        return `${hours}hr ${mins}min`;
      }
      return `${mins}min`;
    };

    const dayBuckets = new Map();
    for (let i = 14; i >= 0; i -= 1) {
      const dateKey = moment()
        .tz(DASHBOARD_TIMEZONE)
        .subtract(i, "days")
        .format("YYYY-MM-DD");
      dayBuckets.set(dateKey, 0);
    }

    filteredLogs.forEach((log) => {
      const dateKey = moment(log.created_time)
        .tz(DASHBOARD_TIMEZONE)
        .format("YYYY-MM-DD");
      if (dayBuckets.has(dateKey)) {
        dayBuckets.set(dateKey, (dayBuckets.get(dateKey) || 0) + 1);
      }
    });

    const voicemailVolumeData = Array.from(dayBuckets.entries()).map(
      ([dateKey, value]) => ({
        day: moment(dateKey).format("DD"),
        value,
      }),
    );

    const userMap = new Map();
    filteredLogs.forEach((log) => {
      const name = log.email_from_name || "Unknown User";
      const email = log.to_email_addresses || "-";
      const key = `${name}|${email}`;

      if (!userMap.has(key)) {
        userMap.set(key, { name, email, messages: 0 });
      }

      userMap.get(key).messages += 1;
    });

    const topUsers = Array.from(userMap.values())
      .sort((a, b) => b.messages - a.messages)
      .slice(0, 5)
      .map((user, index) => ({
        rank: index + 1,
        ...user,
      }));

    const now = moment.tz(DASHBOARD_TIMEZONE);
    const cycleStart = now.clone().startOf("month");
    const cycleEnd = now.clone();
    const voicemailsThisMonth = filteredLogs.filter((log) => {
      const createdAt = parseLogDate(log);
      return (
        createdAt &&
        createdAt.isSameOrAfter(cycleStart) &&
        createdAt.isSameOrBefore(cycleEnd)
      );
    }).length;
    const planLimit = Math.max(0, toNumber(currentPlan?.voicemailLimit, 0));
    const planUsedPercent =
      planLimit > 0 ? Math.round((voicemailsThisMonth / planLimit) * 100) : 0;

    const metricCards = [
      {
        title: "Voicemails Transcribed",
        value: formatNumber(totalLogs),
        subtitle: `${planUsedPercent}% of Plan Total`,
        subtitleColor: "orange",
      },
      {
        title: "Transcription Minutes",
        value: formatMinutesToReadable(Math.round(durationSecondsTotal / 60)),
        subtitle: `${Math.round(avgMessageDuration)}s avg duration`,
        subtitleColor: "blue",
      },
      {
        title: "SMS Transcriptions",
        value: formatNumber(smsTranscriptions),
        subtitle: `${formatNumber(deliveredCount)} delivered`,
        subtitleColor: "bright",
      },
      {
        title: "Success Rate",
        value: `${successRate.toFixed(1)}%`,
        subtitle: `${failureRate.toFixed(1)}% failures`,
        subtitleColor: "green",
      },
    ];

    const transcriptionStats = [
      {
        icon: VerifiedOutlinedIcon,
        iconColor: "success",
        value: `${successRate.toFixed(1)}%`,
        label: "Success Rate",
        comparison: "-",
        comparisonType: "neutral",
      },
      {
        icon: AnnouncementOutlinedIcon,
        iconColor: "statsRed",
        value: `${failureRate.toFixed(1)}%`,
        label: "Failure Rate",
        comparison: "-",
        comparisonType: "neutral",
      },
      {
        icon: SpeedIcon,
        iconColor: "statsOrange",
        value: `${avgProcessingSeconds.toFixed(1)}s`,
        label: "Avg Processing Time",
        comparison: "-",
        comparisonType: "neutral",
      },
      {
        icon: TimerIcon,
        iconColor: "statsBright",
        value: `${Math.round(avgMessageDuration)}s`,
        label: "Avg Message Duration",
        comparison: "-",
        comparisonType: "neutral",
      },
    ];

    return {
      metricCards,
      voicemailVolumeData,
      transcriptionStats,
      topUsers,
    };
  }, [currentPlan?.voicemailLimit, filteredLogs]);

  const showDashboardLoading = isLoading && filteredLogs.length === 0;

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <div className="flex items-center gap-1">
            <span>DATE/TIME</span>
            <Tooltip text="Date & time PBXScribe received voicemail message">
              <InfoIcon sx={{ fontSize: "1rem", color: "gray" }} />
            </Tooltip>
          </div>
        ),
        accessor: "createdDateTime",
        Cell: ({ value }) => {
          const date = moment(value, "YYYY-MM-DD HH:mm:ss");
          return (
            <span className="text-content">
              {date.format("MM/DD/YYYY hh:mm A")}
            </span>
          );
        },
      },
      {
        Header: "CALLER ID",
        accessor: "callerId",
        Cell: ({ value }) => <span className="text-content">{value}</span>,
      },
      {
        Header: "USER NAME",
        accessor: "userName",
        Cell: ({ value }) => <span className="text-content">{value}</span>,
      },
      {
        Header: () => (
          <div className="flex items-center gap-1">
            <span>DELIVER TO</span>
            <Tooltip text="End user email address to deliver transcribed message to">
              <InfoIcon sx={{ fontSize: "1rem", color: "gray" }} />
            </Tooltip>
          </div>
        ),
        accessor: "deliverTo",
        Cell: ({ value }) => <span className="text-content ">{value}</span>,
      },
      {
        Header: "STATUS",
        accessor: "status",
        disableSortBy: true,
        Cell: ({ value }) => <StatusBadge status={value} />,
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your voicemail transcription activity"
      />

      <div className="mb-8">
        <MetricCard metrics={dashboardData.metricCards} isLoading={showDashboardLoading} />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <VoicemailVolumeChart chartData={dashboardData.voicemailVolumeData} />
        <TranscriptionStats stats={dashboardData.transcriptionStats} />
      </div>

      {/* Top Users Section */}
      <div className="mb-6">
        <TopUsers users={dashboardData.topUsers} />
      </div>

      {/* Data Table Section */}
      <div className="mb-6">
        <div
          className={`border border-line rounded-xl shadow-sm bg-white overflow-hidden logs-table-area${
            spreadTable ? " table-zoom-out" : ""
          }`}
        >
          <LogsToolbar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            spreadTable={spreadTable}
            setSpreadTable={setSpreadTable}
            setShowFilterModal={setShowFilterModal}
            statusFilter={deliveryStatusFilter}
            setStatusFilter={setDeliveryStatusFilter}
            statusOptions={statusOptions}
            displayLogs={displayLogs}
            columns={columns}
          />

          <FilterModal
            isOpen={showFilterModal}
            onClose={() => setShowFilterModal(false)}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            statusFilter={deliveryStatusFilter}
            setStatusFilter={setDeliveryStatusFilter}
            dateRange={dateRange}
            setDateRange={setDateRange}
            statusOptions={statusOptions}
            dateRangeOptions={dateRangeOptions}
          />

          {!isLoading && displayLogs.length === 0 ? (
            <div className="w-full h-full min-h-[250px] px-6 py-10 bg-white rounded-b-xl text-center flex flex-col items-center justify-center">
              <p className="text-base lg:text-lg font-semibold text-primary mb-1">
                No logs available
              </p>
              <p className="text-xs lg:text-base text-gray-500">
                {hasAnyLogs
                  ? ""
                  : "There are no logs to display. Please check back later when messages have been processed."}
              </p>
            </div>
          ) : (
            <div className="overflow-auto">
              <Table
                columns={columns}
                data={displayLogs}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pageSize={10}
                showTotalRecords={true}
                totalRecords={displayLogs.length}
                showPageSizeSelector={true}
                useMUIicons={false}
                loading={isLoading}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
